import {
  DateFormat,
  formatDate,
  formatName,
  getTimeDifference,
  ProfileImage,
  Rating,
  TimeUnit,
} from '@leland-dev/leland-ui-library';
import Link from 'next/link';
import React, { useState } from 'react';

import { PackageVisibility } from '../../__generated-gql-types__/globalTypes';
import { getCoachUrl } from '../../utils/coach';
import { useCountLines } from '../../utils/hooks/useCountLines';

import { ReviewOutcome } from './ReviewOutcome';
import { type ReviewReviewFragment } from './__generated-gql-types__/Review.generated';

interface ReviewProps {
  review: ReviewReviewFragment;
  viewAsCoach?: boolean;
  hideFor?: boolean;
  hideProfilePicture?: boolean;
  hideCategory?: boolean;
  hideOfferings?: boolean;
  className?: string;
  hideHeadline?: boolean;
  hideOutcome?: boolean;
  showCoachAttribution?: boolean;
}

const Review: React.FC<ReviewProps> = ({
  review,
  viewAsCoach,
  hideProfilePicture: hideProfilePictureProp,
  hideFor,
  hideCategory,
  className = '',
  hideHeadline,
  hideOutcome,
  showCoachAttribution,
}) => {
  const [commentLines, commentRef] = useCountLines();

  const [expandedComment, setExpandedComment] = useState(false);

  const anonymous = review.anonymous;

  const hideProfilePicture = hideProfilePictureProp || anonymous;

  const categoryReview = review.categoryReviews?.[0] ?? null;

  const outcomesToUse = review.outcomes;

  const clientNameForOutcome =
    (anonymous
      ? 'Client'
      : review.guestReviewer !== null
        ? review.guestReviewer
        : review.applicant?.user.firstName) ?? 'Client';

  const userForProfileImg = viewAsCoach
    ? review.applicant?.user
    : review.coach.user;

  const isWithinTwoYears =
    getTimeDifference(review.reviewedAt, Date.now(), TimeUnit.YEAR) < 2;

  const mostRecentOrder = review.mostRecentOrder;
  const getOrderText = () => {
    if (mostRecentOrder?.packageOrder) {
      return `Purchased package${
        mostRecentOrder.packageOrder.visibility === PackageVisibility.PUBLIC
          ? `: ${mostRecentOrder.packageOrder.name}`
          : mostRecentOrder.packageOrder.category
            ? ` (${mostRecentOrder.packageOrder.category.name})`
            : ''
      }`;
    } else if (mostRecentOrder?.classOrder) {
      return `Attended class${
        mostRecentOrder?.classOrder.coachingSession.content.__typename ===
        'Class'
          ? `: ${mostRecentOrder.classOrder.coachingSession.content.name}`
          : ''
      }`;
    } else if (
      review.mostRecentClassSession &&
      review.mostRecentClassSession.content?.__typename === 'Class' &&
      review.mostRecentClassSession.content.isFreeEvent
    ) {
      return `Attended event: ${review.mostRecentClassSession.content?.name}`;
    } else {
      return `Received coaching for ${categoryReview?.category.name}${
        categoryReview?.subCategoryList?.length
          ? ` (${categoryReview.subCategoryList[0].name})`
          : ''
      }`;
    }
  };
  return (
    <div className={`flex flex-col items-start justify-center ${className}`}>
      <div className="items-center md:flex md:space-x-2">
        <Rating
          rate={review.rate}
          overallRate={review.overallRate}
          showRate
          dimensionRates={
            categoryReview
              ? {
                  knowledge: categoryReview.knowledge,
                  value: categoryReview.value,
                  responsiveness: categoryReview.responsiveness,
                  supportiveness: categoryReview.supportiveness,
                }
              : undefined
          }
        />
        {!hideHeadline && review.headline ? (
          <h3 className={`text-lg font-medium leading-6 text-leland-gray-dark`}>
            {review.headline}
          </h3>
        ) : null}
      </div>
      {review.comment ? (
        <div className="text-leland-gray-dark">
          <p
            ref={commentRef}
            id={`${review.id}-comment`}
            className={`box-border whitespace-pre-line text-lg ${
              !expandedComment && commentLines >= 4 && 'line-clamp-4'
            }`}
          >
            {review.comment}
          </p>
          {commentLines > 4 ? (
            <button
              className="link link--primary text-lg"
              onClick={() => setExpandedComment((prevState) => !prevState)}
            >
              {expandedComment ? 'View less' : 'View more'}
            </button>
          ) : null}
        </div>
      ) : null}
      {viewAsCoach ? (
        <div className="mt-4 flex flex-row items-center text-sm">
          {!hideProfilePicture ? (
            <ProfileImage
              className="mr-3 size-9"
              pictureLink={userForProfileImg?.pictureLink ?? ''}
              firstName={userForProfileImg?.firstName ?? ''}
              lastName={userForProfileImg?.lastName ?? ''}
              anonymous={anonymous || review.guestReviewer !== null}
            />
          ) : null}
          <div className="flex flex-col">
            <div className="flex flex-row space-x-2 text-lg ">
              <p className="flex items-center font-medium text-leland-gray-dark">
                {anonymous
                  ? 'Anonymous'
                  : review.guestReviewer !== null
                    ? review.guestReviewer
                    : formatName({
                        firstName: review.applicant?.user.firstName ?? '',
                        lastName: review.applicant?.user.lastName ?? '',
                        shouldTruncateLastName: true,
                      })}
              </p>
              {isWithinTwoYears ? (
                <p className="font-normal text-leland-gray-extra-light">
                  {formatDate(review.reviewedAt, DateFormat.LONG_MONTH_YEAR)}
                </p>
              ) : null}
            </div>
            {showCoachAttribution ? (
              <p className="text-base font-normal text-leland-gray-light">
                Coached by{' '}
                <Link
                  className="underline"
                  target="_blank"
                  href={getCoachUrl({
                    coachSlug: review.coach.slug,
                    categorySlug: categoryReview?.category.slug,
                  })}
                >
                  {formatName(review.coach.user)}
                </Link>
              </p>
            ) : !hideCategory && review.categoryReviews ? (
              <p className="text-base font-normal text-leland-gray-light">
                {getOrderText()}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
      {!hideFor ? (
        <p className="mt-2 text-sm font-normal text-leland-primary">
          <span className="italic text-leland-gray-dark">for </span>
          {formatName({ ...review.coach.user, shouldTruncateLastName: false })}
        </p>
      ) : null}
      {!hideOutcome && outcomesToUse ? (
        <ReviewOutcome
          className="mt-6"
          outcomes={outcomesToUse}
          clientName={clientNameForOutcome}
        />
      ) : null}
    </div>
  );
};

export default Review;
